import React from 'react';
import { createPortal } from 'react-dom';
import styles from './styles/ImageDialog.module.scss';
import { Toggle } from '@fluentui/react/lib/Toggle';

interface IImageResizeDialogProps {
  visible: boolean;
  initialWidth: string;
  initialHeight: string;
  initialIsPreview: boolean;
  initialAlt: string;
  onApply: (width: string, height: string, isPreview: boolean, alt: string) => void;
  onCancel: () => void;
}

interface IImageResizeDialogState {
  width: string;
  height: string;
  isPreview: boolean;
  keepProportions: boolean;
  alt: string;
}

export default class ImageResizeDialog extends React.Component<IImageResizeDialogProps, IImageResizeDialogState> {
  private widthInputRef = React.createRef<HTMLInputElement>();
  private aspectRatio: number = 0; // width / height

  constructor(props: IImageResizeDialogProps) {
    super(props);
    this.state = {
      width: props.initialWidth,
      height: props.initialHeight,
      isPreview: props.initialIsPreview,
      keepProportions: true,
      alt: props.initialAlt,
    };
  }

  componentDidUpdate(prevProps: IImageResizeDialogProps): void {
    // Dialog wurde gerade geöffnet - Werte zurücksetzen und fokussieren
    if (!prevProps.visible && this.props.visible) {
      const w = parseFloat(this.props.initialWidth);
      const h = parseFloat(this.props.initialHeight);
      this.aspectRatio = (w > 0 && h > 0) ? w / h : 0;
      this.setState({ width: this.props.initialWidth, height: this.props.initialHeight, isPreview: this.props.initialIsPreview, keepProportions: true, alt: this.props.initialAlt });
      setTimeout(() => {
        if (this.widthInputRef.current) {
          this.widthInputRef.current.focus();
          this.widthInputRef.current.select();
        }
      }, 0);
    }
  }

  private handleWidthChange = (value: string): void => {
    if (this.state.keepProportions && this.aspectRatio > 0) {
      const w = parseFloat(value);
      const newHeight = Number.isFinite(w) && w > 0 ? String(Math.round(w / this.aspectRatio)) : this.state.height;
      this.setState({ width: value, height: newHeight });
    } else {
      this.setState({ width: value });
    }
  };

  private handleHeightChange = (value: string): void => {
    if (this.state.keepProportions && this.aspectRatio > 0) {
      const h = parseFloat(value);
      const newWidth = Number.isFinite(h) && h > 0 ? String(Math.round(h * this.aspectRatio)) : this.state.width;
      this.setState({ height: value, width: newWidth });
    } else {
      this.setState({ height: value });
    }
  };

  private handleApply = (): void => {
    this.props.onApply(this.state.width, this.state.height, this.state.isPreview, this.state.alt);
  };

  private handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      this.props.onCancel();
    }
  };

  render(): React.ReactNode {    
    if (!this.props.visible || typeof document === 'undefined') {
      return null;
    }

    const UI_COLORS = {
      bg: '#000',
      panel: '#000',
      card: 'rgba(0,0,0,.4)',
      border: '#ffd36b',
      borderStrong: '#c77e19',
      text: '#fdf2b4',
      accent: '#f99a00',
      accentHover: '#ffd36b',
      accentBgHover: '#b06601',
      dangerBgHover: '#9f1600',
    };

    const UI_FONT = '"Marmelad", sans-serif';

    const sharedToggleStyles = {
      root: {
        marginTop: 2,
        display: "flex",
        alignItems: "center",        
        flexDirection: "row-reverse",
        label: {
          paddingLeft: 8,
        }
      },      
      label: {
        color: UI_COLORS.text,
        fontFamily: UI_FONT,
        paddingLeft: 8,
      },
      text: {
        color: UI_COLORS.text,
        fontFamily: UI_FONT,
        marginLeft: 8,
      },
      pill: {
        background: UI_COLORS.card,
        borderColor: UI_COLORS.border,
        selectors: {
          ':hover': {
            borderColor: UI_COLORS.accent,
            background: UI_COLORS.card,
          },
          ':active': {
            borderColor: UI_COLORS.accent,
            background: UI_COLORS.card,
          },
          ':focus': {
            borderColor: UI_COLORS.accent,
            background: UI_COLORS.card,
          },
          ':focus:after': {
            borderColor: UI_COLORS.accent,
            background: UI_COLORS.card
          },
        },
      },
      thumb: {
        background: UI_COLORS.text + " !important",
        ':hover': {            
          background: UI_COLORS.accent,
        },
        ':active': {            
          background: UI_COLORS.accent,
        },
        ':focus': {            
          background: UI_COLORS.accent,
        },
        ':focus:after': {            
          background: UI_COLORS.accent
        },
      },
    };
    const sharedToggleCheckedStyles = {
      root: {        
        marginTop: 2,
        display: "flex",
        alignItems: "center",        
        flexDirection: "row-reverse",
        label: {
          paddingLeft: 8,
        }
      },      
      label: {
        color: UI_COLORS.text,
        fontFamily: UI_FONT,
        paddingLeft: 8,
      },
      text: {
        color: UI_COLORS.text,
        fontFamily: UI_FONT,
      },
      pill: {
        background: UI_COLORS.card,
        borderColor: UI_COLORS.accent,
        selectors: {
          ':hover': {
            borderColor: UI_COLORS.accentBgHover,
            background: UI_COLORS.card,
          },
          ':active': {
            borderColor: UI_COLORS.accentBgHover,
            background: UI_COLORS.card,
          },
          ':focus': {
            borderColor: UI_COLORS.accentBgHover,
            background: UI_COLORS.card,
          },
          ':focus:after': {
            borderColor: UI_COLORS.accentBgHover,
            background: UI_COLORS.card
          },
        },
      },
      thumb: {
        background: UI_COLORS.accent + " !important",
        ':hover': {            
          background: UI_COLORS.accentBgHover,
        },
        ':active': {            
          background: UI_COLORS.accentBgHover,
        },
        ':focus': {            
          background: UI_COLORS.accentBgHover,
        },
        ':focus:after': {            
          background: UI_COLORS.accentBgHover
        },
      },
    };

    return createPortal(
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.65)',
          zIndex: 10004,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
        onClick={this.handleBackdropClick}
      >
        <div
          className={styles.dialog}
          style={{
            border: '1px solid #f99a00',
            borderRadius: 6,                 
            color: '#fdf2b4',
            width: 420,
            height: "fit-content",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.dialogHeader}><span>Bildgr&ouml;&szlig;e anpassen</span></div>
          <div className="mw-form" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8, padding: "12px 16px" }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <div className="mw-fbox-text">
                <label htmlFor="quill-image-width" style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Breite</label>
                <input
                  ref={this.widthInputRef}
                  id="quill-image-width"
                  type="number"
                  min={1}
                  step={1}
                  value={this.state.width}
                  onChange={(e) => this.handleWidthChange(e.target.value)}
                  style={{ width: "unset", height: 32, margin: 0, boxSizing: 'border-box' }}
                />
              </div>              
              <div className="mw-fbox-text">
                <label htmlFor="quill-image-height" style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>H&ouml;he</label>
                <input
                  id="quill-image-height"
                  type="number"
                  min={1}
                  step={1}
                  value={this.state.height}
                  onChange={(e) => this.handleHeightChange(e.target.value)}
                  style={{ width: "unset", height: 32, margin: 0, boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <div className="mw-fbox-text" style={{ width: '100%' }}>
              <label htmlFor="quill-image-alt" style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Alt-Text</label>
              <input
                id="quill-image-alt"
                type="text"
                value={this.state.alt}
                onChange={(e) => this.setState({ alt: e.target.value })}
                style={{ width: '100%', height: 32, margin: 0, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <Toggle
                label="Proportionen beibehalten"
                checked={this.state.keepProportions}
                styles={this.state.keepProportions ? sharedToggleCheckedStyles :sharedToggleStyles}
                onChange={(e, checked?: boolean) => this.setState({ keepProportions: checked ?? false })
                }
              />
              {/*<input
                id="quill-image-keep-proportions"
                type="checkbox"
                checked={this.state.keepProportions}
                onChange={(e) => this.setState({ keepProportions: e.target.checked })}
                style={{ width: 16, height: 16, cursor: 'pointer', margin: 0, boxSizing: 'border-box' }}
              />
              <label htmlFor="quill-image-keep-proportions" style={{ cursor: 'pointer', userSelect: 'none' }}>Proportionen</label> */}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <Toggle
                label="Vorschau"
                checked={this.state.isPreview}
                styles={this.state.isPreview ? sharedToggleCheckedStyles : sharedToggleStyles}
                onChange={(e, checked?: boolean) => this.setState({ isPreview: checked ?? false })
                }
              />
              {/*<input
                id="quill-image-preview"
                type="checkbox"
                checked={this.state.isPreview}
                onChange={(e) => this.setState({ isPreview: e.target.checked })}
                style={{ width: 16, height: 16, cursor: 'pointer', margin: 0, boxSizing: 'border-box' }}
              />
              <label htmlFor="quill-image-preview" style={{ cursor: 'pointer', userSelect: 'none' }}>Vorschau</label>*/}
            </div>
          </div>
          <div className={styles.dialogButtons} style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>            
            <button
              type="button"
              className="mw-button mw-button--ghost mw-button--red mw-button--alert mw-button--small"
              onClick={this.handleApply}
            >
              <div className="mw-button-outer"><div className="mw-button-inner">OK</div></div>
            </button>
            <button
              type="button"
              className="mw-button mw-button--ghost mw-button--small"
              onClick={this.props.onCancel}
            >
              <div className="mw-button-outer"><div className="mw-button-inner">Abbrechen</div></div>
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }
}
